/**
 * React Query hook with IndexedDB caching for Nostr events
 * Provides instant retrieval from cache while fetching fresh data in background
 */

import { useState, useEffect } from 'react';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';
import { nostrCache, generateQueryKey } from '@/lib/nostrCache';

interface UseCachedNostrQueryOptions<TData = NostrEvent[]> {
  /** Nostr filters for the query */
  filters: NostrFilter[];
  /** Relay URLs to query (optional, uses default if not provided) */
  relays?: string[];
  /** Query timeout in milliseconds */
  timeout?: number;
  /** Maximum age of cached data in milliseconds (default: 5 minutes) */
  cacheMaxAge?: number;
  /** Whether to use stale cache data while revalidating */
  useStaleWhileRevalidate?: boolean;
  /** Additional React Query options */
  queryOptions?: Omit<UseQueryOptions<NostrEvent[], Error, TData>, 'queryKey' | 'queryFn'>;
}

/**
 * Query Nostr events with IndexedDB caching
 * Returns cached data instantly while fetching fresh data in the background
 */
export function useCachedNostrQuery<TData = NostrEvent[]>({
  filters,
  relays,
  timeout = 8000,
  cacheMaxAge = 5 * 60 * 1000,
  useStaleWhileRevalidate = true,
  queryOptions = {},
}: UseCachedNostrQueryOptions<TData>): UseQueryResult<TData> {
  const { nostr } = useNostr();
  const [initialData, setInitialData] = useState<NostrEvent[] | undefined>(undefined);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const cacheKey = generateQueryKey(filters);
  const queryKey = ['nostr-cached', cacheKey, relays];

  // Pre-fetch from IndexedDB on mount
  useEffect(() => {
    let mounted = true;

    const loadFromCache = async () => {
      try {
        const cachedEvents = await nostrCache.getCachedQuery(cacheKey, cacheMaxAge);
        if (mounted && cachedEvents && cachedEvents.length > 0) {
          console.log('[Cache] Loaded', cachedEvents.length, 'events from IndexedDB instantly');
          setInitialData(cachedEvents);
        }
      } catch (error) {
        console.warn('[Cache] Failed to load from IndexedDB:', error);
      } finally {
        if (mounted) {
          setInitialDataLoaded(true);
        }
      }
    };

    loadFromCache();

    return () => {
      mounted = false;
    };
  }, [cacheKey, cacheMaxAge]);

  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      console.log('[Cache] Fetching from relays:', { cacheKey: cacheKey.substring(0, 50) + '...' });

      // Fetch fresh data from relays
      const events = relays
        ? await nostr.group(relays).query(filters, {
            signal: AbortSignal.any([signal, AbortSignal.timeout(timeout)]),
          })
        : await nostr.query(filters, {
            signal: AbortSignal.any([signal, AbortSignal.timeout(timeout)]),
          });

      console.log('[Cache] Fetched', events.length, 'events from relays');

      // Store in cache
      try {
        await nostrCache.putEvents(events);
        await nostrCache.cacheQuery(
          cacheKey,
          events.map((e) => e.id)
        );
        console.log('[Cache] Stored in IndexedDB');
      } catch (error) {
        console.warn('[Cache] Failed to store in cache:', error);
      }

      return events;
    },
    // Use cached data as initial data for instant rendering
    initialData: initialDataLoaded ? initialData : undefined,
    staleTime: cacheMaxAge,
    gcTime: cacheMaxAge * 2,
    refetchOnMount: useStaleWhileRevalidate && initialData ? 'always' : true,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });
}

/**
 * Query a single Nostr event with caching
 */
export function useCachedNostrEvent(
  eventId: string | undefined,
  options: Omit<UseCachedNostrQueryOptions, 'filters'> = {}
) {
  return useCachedNostrQuery({
    ...options,
    filters: eventId ? [{ ids: [eventId] }] : [],
    queryOptions: {
      ...options.queryOptions,
      enabled: !!eventId && (options.queryOptions?.enabled ?? true),
      select: (events) => events[0],
    },
  });
}

/**
 * Query author metadata (kind 0) with caching
 */
export function useCachedAuthorMetadata(
  pubkeys: string[],
  options: Omit<UseCachedNostrQueryOptions, 'filters'> = {}
) {
  return useCachedNostrQuery({
    ...options,
    filters: pubkeys.length > 0 ? [{ kinds: [0], authors: pubkeys }] : [],
    queryOptions: {
      ...options.queryOptions,
      enabled: pubkeys.length > 0 && (options.queryOptions?.enabled ?? true),
    },
  });
}

/**
 * Initialize cache and perform cleanup on app start
 */
export async function initializeNostrCache() {
  try {
    await nostrCache.init();
    // Clear entries older than 7 days
    await nostrCache.clearOldEntries(7 * 24 * 60 * 60 * 1000);

    const stats = await nostrCache.getStats();
    console.log('Nostr cache initialized:', stats);
  } catch (error) {
    console.error('Failed to initialize Nostr cache:', error);
  }
}
